import React, { useEffect, useMemo, type MutableRefObject } from 'react';
import isEqual from 'lodash/isEqual';
import type { VirtualItem, VirtualizerOptions } from '@tanstack/virtual-core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isShallowEqual } from '@atlassian/jira-platform-shallow-equal/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { ROW_HEIGHT } from '../../../common/constants.tsx';
import { useScrollElement } from '../../../controllers/scroll-state/index.tsx';
import {
	useSSRSafeVirtualizer,
	useShouldUseSSRVirtualizer,
} from '../../../controllers/virtualization/index.tsx';
import {
	useVirtualizationActions,
	type VirtualItemsMap,
} from '../../../controllers/virtualization/rss-store.tsx';

type UseVirtualizerArgs = Parameters<typeof useVirtualizer<HTMLElement, Element>>[0];

export type Props = {
	rows: number;
	// make setTableHeight required and remove totalSizeRef when cleaning up jira_list_virtualizer_table_height
	totalSizeRef: MutableRefObject<number>;
	getItemKey: (index: number) => number | string | bigint;
	options?: Partial<VirtualizerOptions<HTMLElement, Element>>;
};

export type PropsInner = {
	// make setTableHeight required and remove totalSizeRef when cleaning up jira_list_virtualizer_table_height
	totalSizeRef: MutableRefObject<number>;
	options: UseVirtualizerArgs;
};

const estimateSize = () => ROW_HEIGHT + 1; // include border

const InitializeVirtualizerInnerNew = ({ totalSizeRef, options }: PropsInner) => {
	const { setVirtualizer, setVirtualItemsMap } = useVirtualizationActions();
	const virtualizer = useVirtualizer<HTMLElement, Element>(options);
	const totalSize = virtualizer?.getTotalSize();
	const items = virtualizer?.getVirtualItems();

	// eslint-disable-next-line no-param-reassign, react-hooks/react-compiler
	totalSizeRef.current = totalSize ?? 0;

	const prevVirtualizer = usePrevious(virtualizer);
	const prevItems = usePrevious(items);

	if (!isShallowEqual(prevVirtualizer, virtualizer)) {
		setVirtualizer(virtualizer);
	}

	if (!isEqual(prevItems, items)) {
		const virtualItemsMap = items?.reduce((acc: VirtualItemsMap, item: VirtualItem) => {
			acc[String(item.key)] = true;
			return acc;
		}, {});

		setVirtualItemsMap(virtualItemsMap);
	}

	return null;
};

const InitializeVirtualizerInnerOld = ({
	rows = 0,
	getItemKey,
	totalSizeRef,
	options: virtualizerOptions,
}: Props) => {
	const scrollElement = useScrollElement();
	const { setVirtualizer, setVirtualItemsMap } = useVirtualizationActions();

	const options = useMemo(
		() => ({
			count: rows,
			getScrollElement: () => scrollElement,
			estimateSize,
			getItemKey,
			overscan: 10,
			...(fg('jira_spreadsheet_timeline_image_export') && virtualizerOptions),
		}),
		[getItemKey, rows, scrollElement, virtualizerOptions],
	);

	const virtualizer = useSSRSafeVirtualizer(options);
	const totalSize = virtualizer?.getTotalSize();
	const items = virtualizer?.getVirtualItems();

	// eslint-disable-next-line no-param-reassign, react-hooks/react-compiler
	totalSizeRef.current = totalSize ?? 0;

	const prevVirtualizer = usePrevious(virtualizer);
	const prevItems = usePrevious(items);

	if (!isShallowEqual(prevVirtualizer, virtualizer)) {
		setVirtualizer(virtualizer);
	}

	if (!isEqual(prevItems, items)) {
		const virtualItemsMap = items?.reduce((acc: VirtualItemsMap, item: VirtualItem) => {
			acc[String(item.key)] = true;
			return acc;
		}, {});

		setVirtualItemsMap(virtualItemsMap);
	}

	return null;
};

const InitializeVirtualizerNew = ({
	rows = 0,
	getItemKey,
	options: virtualizerOptions,
	...restProps
}: Props) => {
	const [shouldSkipVirtualizer, setSkipVirtualizer] = useShouldUseSSRVirtualizer();

	const scrollElement = useScrollElement();

	const options: UseVirtualizerArgs = useMemo(
		() => ({
			count: rows,
			getScrollElement: () => scrollElement,
			estimateSize,
			getItemKey,
			overscan: 10,
			...(fg('jira_spreadsheet_timeline_image_export') && virtualizerOptions),
		}),
		[getItemKey, rows, scrollElement, virtualizerOptions],
	);

	/**
	 * We enable virtualization when we detect that the user has initiated a scroll
	 */
	useEffect(() => {
		if (!shouldSkipVirtualizer) {
			return;
		}

		const element = scrollElement;

		const onScroll = () => {
			if (!__SERVER__) {
				setSkipVirtualizer(false);
				element?.removeEventListener('scroll', onScroll);
			}
		};
		element?.addEventListener('scroll', onScroll);
		return () => {
			element?.removeEventListener('scroll', onScroll);
		};
	}, [shouldSkipVirtualizer, setSkipVirtualizer, scrollElement]);

	if (__SERVER__ || shouldSkipVirtualizer) {
		return null;
	}

	return <InitializeVirtualizerInnerNew {...restProps} options={options} />;
};

const InitializeVirtualizerOld = (props: Props) => {
	// Skip any attempts to perform virtualization in SSR
	if (__SERVER__) {
		return null;
	}

	return <InitializeVirtualizerInnerOld {...props} />;
};

export const InitializeVirtualizer = componentWithFG(
	'fix_issue_row_remounts',
	InitializeVirtualizerNew,
	InitializeVirtualizerOld,
);
